import { useCallback, useState } from 'react';
import type { Client } from '@/types';

interface UseClientSelectionArgs {
  canMarkVisit: (client: Client) => boolean;
}

interface UseClientSelectionResult {
  isSelectionMode: boolean;
  isExitingSelection: boolean;
  selectedClients: Set<string>;
  selectedCount: number;
  enterSelectionMode: () => void;
  toggleClientSelection: (id: string) => void;
  handleClientLongPress: (client: Client) => boolean;
  selectAllFromClients: (clients: Client[]) => boolean;
  startExitingSelection: () => void;
  exitSelectionMode: () => void;
}

export const useClientSelection = ({
  canMarkVisit,
}: UseClientSelectionArgs): UseClientSelectionResult => {
  const [selectedClients, setSelectedClients] = useState<Set<string>>(() => new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [isExitingSelection, setIsExitingSelection] = useState(false);

  const enterSelectionMode = useCallback(() => {
    setIsSelectionMode(true);
    setIsExitingSelection(false);
  }, []);

  const toggleClientSelection = useCallback((id: string) => {
    setIsSelectionMode(true);
    setSelectedClients((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleClientLongPress = useCallback(
    (client: Client) => {
      if (!canMarkVisit(client)) {
        return false;
      }

      enterSelectionMode();
      setSelectedClients((prev) => {
        if (prev.has(client.id)) {
          return prev;
        }
        const next = new Set(prev);
        next.add(client.id);
        return next;
      });

      return true;
    },
    [canMarkVisit, enterSelectionMode]
  );

  const selectAllFromClients = useCallback(
    (clients: Client[]) => {
      const ids = clients.filter((client) => canMarkVisit(client)).map((client) => client.id);
      const next = new Set(ids);

      enterSelectionMode();
      setSelectedClients(next);

      return next.size > 0;
    },
    [canMarkVisit, enterSelectionMode]
  );

  const startExitingSelection = useCallback(() => {
    setIsExitingSelection((prev) => {
      if (prev) return prev;
      return isSelectionMode ? true : prev;
    });
  }, [isSelectionMode]);

  const exitSelectionMode = useCallback(() => {
    setSelectedClients(new Set());
    setIsSelectionMode(false);
    setIsExitingSelection(false);
  }, []);

  return {
    isSelectionMode,
    isExitingSelection,
    selectedClients,
    selectedCount: selectedClients.size,
    enterSelectionMode,
    toggleClientSelection,
    handleClientLongPress,
    selectAllFromClients,
    startExitingSelection,
    exitSelectionMode,
  };
};
